import axios from "axios";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store";
import { getBreefById } from "../introduction/BreefService";
import { BreefDetails, setBreefDetails } from "./slice";

const API = process.env.REACT_APP_API_BASE_URL;
const linkHeaders = () => ({ headers: { Authorization: `Bearer ${sessionStorage.getItem("BreefAccesstoken") || ""}` } });

/** The breef (with its game). After a page reload the store is empty: fetched again with the breef id. */
export const useBreefDetails = (): BreefDetails | null => {
  const dispatch = useDispatch();
  const fromStore = useSelector((state: RootState) => state.score.breefDetails);
  const [loaded, setLoaded] = useState<BreefDetails | null>(fromStore?.gameId?.gameCode ? fromStore : null);
  useEffect(() => {
    if (fromStore?.gameId?.gameCode) {
      setLoaded(fromStore);
      return;
    }
    const breefId = sessionStorage.getItem("breefId");
    if (!breefId) return;
    getBreefById(breefId)
      .then((breef: BreefDetails) => {
        dispatch(setBreefDetails(breef));
        setLoaded(breef);
      })
      .catch(() => setLoaded(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromStore?.gameId?.gameCode]);
  return loaded;
};

/** The game servers the player app accepts scores from (admin: GAME_ORIGINS). Empty list = the game's own origin. */
let originsPromise: Promise<string[]> | null = null;
export const getGameOrigins = () => {
  if (!originsPromise) {
    originsPromise = fetch(`${API}/v1/public-config`)
      .then((res) => res.json())
      .then((config) => (config?.games?.origins || []) as string[])
      .catch(() => []);
  }
  return originsPromise;
};

/** The real round: the server hands out a round id (only one game per breef) */
export const startGameRound = async (): Promise<{ round: string; seconds: number }> =>
  (await axios.post(`${API}/v1/Breefs/game-round`, {}, linkHeaders())).data;

export interface GameResultData {
  gameScore: number;
  gamePoints: number;
  quizScore: number;
  breefScore: number;
}

/** The game's own score with the round id - the server computes the breef score */
export const submitGameScore = async (round: string, gameScore: number): Promise<GameResultData> => {
  const breefId = sessionStorage.getItem("breefId");
  const participantId = sessionStorage.getItem("InvitedUserId");
  return (await axios.put(`${API}/v1/Breefs/breef-total-score/${participantId}/${breefId}`, { round, gameScore }, linkHeaders())).data;
};

/** Builds the game's address with the protocol parameters (mode, seconds, round, lang) */
export const gameUrl = (gameCode: string, params: Record<string, string | number>) => {
  const url = new URL(gameCode, window.location.href);
  Object.entries(params).forEach(([key, value]) => url.searchParams.set(key, String(value)));
  return url.toString();
};
